import React, {ReactNode, useMemo} from 'react'
import {GlobalStyles} from '@mui/material'
import {alpha, darken, getContrastRatio, lighten, Theme, ThemeProvider, useTheme} from '@mui/material/styles'
import {useAccountStyle} from '@containers/accountStyle/hooks/useAccountStyle'

interface IProps {
	children: ReactNode
}

// Recolours MUI primary/info and the hard-coded map blues (via --account-accent-* vars) to the account colour.
// Vars live on :root while mounted so portals (mobile sidebar drawer, popovers) get them too.
const AccountAccentScope = ({children}: IProps) => {
	const {tokens} = useAccountStyle()
	const accent = tokens.accent
	const outerTheme = useTheme()
	const isDark = outerTheme.palette.mode === 'dark'

	const theme = useMemo(() => (outer: Theme): Theme => {
		const main = outer.palette.augmentColor({color: {main: accent}})
		return {...outer, palette: {...outer.palette, primary: main, info: main}}
	}, [accent])

	const vars = useMemo(() => ({
		'--account-accent': accent,
		'--account-accent-hover': darken(accent, 0.12),
		'--account-accent-light': lighten(accent, 0.45),
		'--account-accent-contrast': getContrastRatio(accent, '#fff') >= 3 ? '#fff' : '#1f1f1f',
		'--account-accent-tint': alpha(accent, 0.3),
		'--account-accent-tint-hover': alpha(accent, 0.42),
		'--account-accent-soft': alpha(accent, 0.12),
		'--account-accent-on-tint': outerTheme.palette.text.primary,
		'--account-accent-text': isDark ? lighten(accent, 0.45) : darken(accent, 0.35),
	}), [accent, isDark, outerTheme])

	return (
		<ThemeProvider theme={theme}>
			<GlobalStyles styles={{':root': vars}}/>
			{children}
		</ThemeProvider>
	)
}

export default AccountAccentScope
